import { useCallback, useEffect, useState, type FormEvent } from 'react';

import {
  ApiError,
  twoFactorApi,
  type TwoFactorSetup,
  type TwoFactorStatus,
} from '../services/api/client';

/**
 * Verificação em dois passos do professor (PRD 39.2). Opcional: o professor
 * liga e desliga aqui. Só aparece para professor (a tela Conta decide), e o
 * backend exige o papel de qualquer forma.
 *
 * Os códigos de recuperação só existem em texto no instante em que são
 * gerados — por isso a tela os mostra uma vez e pede para guardá-los.
 */
export function TwoFactorPanel() {
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [setup, setSetup] = useState<TwoFactorSetup | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [mode, setMode] = useState<'idle' | 'disable' | 'renew'>('idle');

  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setStatus(await twoFactorApi.status());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao carregar o estado.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function reset() {
    setCode('');
    setPassword('');
    setError('');
    setMode('idle');
  }

  async function run(action: () => Promise<void>, fallback: string) {
    setError('');
    setBusy(true);

    try {
      await action();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : fallback);
    } finally {
      setBusy(false);
    }
  }

  const startSetup = () =>
    run(async () => {
      setSetup(await twoFactorApi.setup());
      setCode('');
    }, 'Falha ao iniciar a ativação.');

  const confirmEnable = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      const result = await twoFactorApi.enable(code);
      setRecoveryCodes(result.recoveryCodes);
      setSetup(null);
      reset();
      await load();
    }, 'Falha ao ativar.');
  };

  const confirmDisable = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      await twoFactorApi.disable(password, code);
      reset();
      setRecoveryCodes(null);
      await load();
    }, 'Falha ao desativar.');
  };

  const confirmRenew = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      const result = await twoFactorApi.regenerateRecoveryCodes(password, code);
      setRecoveryCodes(result.recoveryCodes);
      reset();
      await load();
    }, 'Falha ao gerar novos códigos.');
  };

  if (!status) {
    return (
      <section className="card">
        <h3>Verificação em dois passos</h3>
        {error ? <p className="form-error">{error}</p> : <p className="muted">Carregando…</p>}
      </section>
    );
  }

  return (
    <section className="card">
      <h3>Verificação em dois passos</h3>

      {/* Códigos de recuperação recém-gerados: aparecem uma vez só. */}
      {recoveryCodes && (
        <div className="two-factor-codes">
          <p>
            <strong>Guarde estes códigos de recuperação agora.</strong> Eles não serão mostrados de
            novo. Cada um vale uma vez e serve se você perder o celular.
          </p>
          <ul className="two-factor-code-list">
            {recoveryCodes.map((item) => (
              <li key={item}>
                <code>{item}</code>
              </li>
            ))}
          </ul>
          <div className="button-row">
            <button
              type="button"
              onClick={() => void navigator.clipboard?.writeText(recoveryCodes.join('\n'))}
            >
              Copiar
            </button>
            <button type="button" className="primary" onClick={() => setRecoveryCodes(null)}>
              Já guardei
            </button>
          </div>
        </div>
      )}

      {!status.enabled && !setup && (
        <>
          <p className="muted small">
            Além da senha, pede um código de 6 dígitos gerado no seu celular. Protege a conta de
            professor, que dá acesso a todas as turmas, mesmo que a senha vaze. Funciona com o
            Microsoft Authenticator, o Google Authenticator e similares.
          </p>
          {error && <p className="form-error">{error}</p>}
          <div className="button-row">
            <button type="button" className="primary" disabled={busy} onClick={() => void startSetup()}>
              Ativar verificação em dois passos
            </button>
          </div>
        </>
      )}

      {!status.enabled && setup && (
        <form onSubmit={confirmEnable} noValidate>
          <ol className="two-factor-steps">
            <li>No aplicativo autenticador, adicione uma conta (&quot;Outra conta&quot;) e leia o QR Code.</li>
            <li>Digite abaixo o código de 6 dígitos que o aplicativo mostrar.</li>
          </ol>

          <div
            className="qr-code"
            // SVG gerado pelo nosso backend a partir de uma URL que ele mesmo
            // monta — não há conteúdo de terceiros aqui (mesmo caso do convite).
            dangerouslySetInnerHTML={{ __html: setup.qrCodeSvg }}
          />
          <p className="muted small">
            Sem câmera? Digite esta chave no aplicativo: <code>{setup.secret}</code>
          </p>

          <label htmlFor="tf-enable-code">Código de 6 dígitos</label>
          <input
            id="tf-enable-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />

          {error && <p className="form-error">{error}</p>}

          <div className="button-row" style={{ marginTop: '0.75rem' }}>
            <button type="submit" className="primary" disabled={busy || code.trim().length < 6}>
              Confirmar e ativar
            </button>
            <button
              type="button"
              onClick={() => {
                setSetup(null);
                reset();
              }}
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      {status.enabled && mode === 'idle' && (
        <>
          <p className="muted small">
            <strong>Ativa.</strong> Restam {status.recoveryCodesLeft} de 10 códigos de recuperação.
            {status.recoveryCodesLeft <= 3 && ' Considere gerar novos.'}
          </p>
          <div className="button-row">
            <button type="button" onClick={() => setMode('renew')}>
              Gerar novos códigos de recuperação
            </button>
            <button type="button" className="link-danger" onClick={() => setMode('disable')}>
              Desativar
            </button>
          </div>
        </>
      )}

      {status.enabled && mode !== 'idle' && (
        <form onSubmit={mode === 'disable' ? confirmDisable : confirmRenew} noValidate>
          <p className="muted small">
            {mode === 'disable'
              ? 'Para desativar, confirme com a senha e um código.'
              : 'Os códigos antigos deixam de valer. Confirme com a senha e um código.'}
          </p>

          <label htmlFor="tf-password">Senha</label>
          <input
            id="tf-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <label htmlFor="tf-code">Código (do aplicativo ou de recuperação)</label>
          <input
            id="tf-code"
            type="text"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />

          {error && <p className="form-error">{error}</p>}

          <div className="button-row" style={{ marginTop: '0.75rem' }}>
            <button
              type="submit"
              className="primary"
              disabled={busy || password === '' || code.trim() === ''}
            >
              {mode === 'disable' ? 'Desativar' : 'Gerar novos códigos'}
            </button>
            <button type="button" onClick={reset}>
              Cancelar
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
